import { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Share, Plus, Smartphone, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const AddToHomescreen = forwardRef(function AddToHomescreen({ showOnLoad = true }, ref) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  
  // Calculate standalone and iOS once on mount
  const isStandalone = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
  }, []);
  
  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  const isAndroid = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /Android/i.test(navigator.userAgent);
  }, []);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    openPrompt: () => setShowPrompt(true),
    isInstalled: () => isStandalone
  }));

  useEffect(() => {
    // Check if user already dismissed
    const dismissed = localStorage.getItem("pwa-prompt-dismissed");
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Listen for beforeinstallprompt (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    // Only auto-show if showOnLoad is true
    if (!showOnLoad) {
      return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
    }

    // Show again after 7 days
    if (dismissed && daysSinceDismissed < 7) {
      return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
    }

    // Don't show if already installed
    if (isStandalone) {
      return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
    }

    // Show prompt after 2 seconds
    const showTimer = setTimeout(() => setShowPrompt(true), 2000);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      clearTimeout(showTimer);
    };
  }, [showOnLoad, isStandalone]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Show native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        setShowPrompt(false);
        localStorage.setItem("pwa-prompt-dismissed", Date.now().toString());
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem("pwa-prompt-dismissed", Date.now().toString());
  };

  // Don't render if standalone or prompt not shown
  if (isStandalone || !showPrompt) return null;

  return (
    <AnimatePresence>
      {showPrompt && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={handleDismiss}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.95 }}
            className="fixed bottom-4 left-4 right-4 md:bottom-8 md:left-auto md:right-8 md:w-96 z-50"
          >
            <div className="glass-card rounded-2xl p-6 border border-white/10 shadow-2xl">
              {/* Close Button */}
              <button
                onClick={handleDismiss}
                className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
                data-testid="pwa-dismiss"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Icon */}
              <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-500 mb-4 mx-auto">
                <Smartphone className="h-8 w-8 text-white" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-foreground text-center mb-2">
                Add UPNEXT to Homescreen
              </h3>

              {/* Description */}
              <p className="text-muted-foreground text-center text-sm mb-6">
                Akses UPNEXT dengan lebih pantas! Install sebagai app untuk pengalaman terbaik.
              </p>

              {/* Benefits */}
              <div className="space-y-2 mb-6">
                {[
                  "Akses pantas dari homescreen",
                  "Notifikasi events terkini",
                  "Boleh guna offline"
                ].map((benefit, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Star className="h-4 w-4 text-amber-400 flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>

              {/* iOS Instructions */}
              {isIOS ? (
                <div className="space-y-4">
                  <div className="bg-white/5 rounded-xl p-4">
                    <p className="text-sm text-foreground font-medium mb-3">📱 Cara install di iPhone/iPad:</p>
                    <ol className="space-y-3 text-sm text-muted-foreground">
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                        <div>
                          <span className="text-foreground font-medium">Tap butang Share</span>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="w-8 h-8 rounded-lg bg-sky-500 flex items-center justify-center">
                              <Share className="h-4 w-4 text-white" />
                            </div>
                            <span className="text-xs text-muted-foreground">di bar bawah Safari</span>
                          </div>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                        <div>
                          <span className="text-foreground font-medium">Scroll bawah, tap "Add to Home Screen"</span>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="w-8 h-8 rounded-lg bg-gray-600 flex items-center justify-center">
                              <Plus className="h-4 w-4 text-white" />
                            </div>
                            <span className="text-xs text-muted-foreground">cari icon ini</span>
                          </div>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                        <div>
                          <span className="text-foreground font-medium">Tap "Add" untuk confirm</span>
                          <p className="text-xs text-muted-foreground mt-1">UPNEXT akan ada di homescreen anda! 🎉</p>
                        </div>
                      </li>
                    </ol>
                  </div>
                  <Button
                    onClick={handleDismiss}
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl h-12"
                  >
                    ✓ Faham, Saya Akan Buat!
                  </Button>
                </div>
              ) : isAndroid && !deferredPrompt ? (
                /* Android Manual Instructions (if native prompt not available) */
                <div className="space-y-4">
                  <div className="bg-white/5 rounded-xl p-4">
                    <p className="text-sm text-foreground font-medium mb-3">📱 Cara install di Android:</p>
                    <ol className="space-y-3 text-sm text-muted-foreground">
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                        <div>
                          <span className="text-foreground font-medium">Tap menu ⋮ di Chrome</span>
                          <p className="text-xs text-muted-foreground mt-1">3 titik di kanan atas browser</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                        <div>
                          <span className="text-foreground font-medium">Tap "Add to Home screen"</span>
                          <p className="text-xs text-muted-foreground mt-1">atau "Install app"</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                        <div>
                          <span className="text-foreground font-medium">Tap "Add" untuk confirm</span>
                          <p className="text-xs text-muted-foreground mt-1">UPNEXT akan ada di homescreen anda! 🎉</p>
                        </div>
                      </li>
                    </ol>
                  </div>
                  <Button
                    onClick={handleDismiss}
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl h-12"
                  >
                    ✓ Faham, Saya Akan Buat!
                  </Button>
                </div>
              ) : (
                /* Android/Desktop with Native Install Prompt */
                <div className="space-y-3">
                  {isAndroid && (
                    <p className="text-xs text-center text-muted-foreground mb-2">
                      📱 Tekan butang di bawah untuk install UPNEXT ke homescreen anda
                    </p>
                  )}
                  <Button
                    onClick={handleInstall}
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl h-12"
                    data-testid="pwa-install"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    Install Sekarang
                  </Button>
                  <Button
                    onClick={handleDismiss}
                    variant="ghost"
                    className="w-full text-muted-foreground hover:text-foreground rounded-xl"
                  >
                    Nanti dulu
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
});

export default AddToHomescreen;
